import { QueryObserver } from '@tanstack/query-core';

import { fetchFinancialConfig } from '../../predict/src/core/api/financial-config';
import { queryClient } from '../core/queryClient';

export type FetchFinancialConfigResult = Awaited<ReturnType<typeof fetchFinancialConfig>>;

export interface FinancialConfigResource {
  workspaceId: string;
  entityType: 'organisation' | 'consolidation';
  entityId: string;
}

const CACHE_TIME = 1_800_000; // 30 minutes in milliseconds

export const financialConfigQueryKey = ({ workspaceId, entityType, entityId }: FinancialConfigResource) =>
  ['financial-config', workspaceId, entityType, entityId] as const;

export const financialConfigQueryOptions = (resource: FinancialConfigResource) => ({
  queryKey: financialConfigQueryKey(resource),
  queryFn: () =>
    fetchFinancialConfig(resource.workspaceId, resource.entityId, resource.entityType === 'consolidation'),
  gcTime: CACHE_TIME,
  refetchOnMount: true,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  retry: false as const,
  retryOnMount: false,
  staleTime: CACHE_TIME
});

export const financialConfigQuery = (
  resource: FinancialConfigResource
): QueryObserver<FetchFinancialConfigResult> =>
  new QueryObserver<FetchFinancialConfigResult>(queryClient, financialConfigQueryOptions(resource));

export const clearFinancialConfigQueryCache = async (resource: FinancialConfigResource): Promise<void> => {
  const filters = { queryKey: financialConfigQueryKey(resource), exact: true };
  // An initial request may predate the completed update, even when it has no cached data yet.
  await queryClient.cancelQueries(filters);
  await queryClient.invalidateQueries(filters, { throwOnError: true });
};
